import { SortDirection } from '@angular/material/sort';
import {
    ColumnConfig,
    GridFiltersStorageService,
    GridSortState,
    GridStateService,
    queryStatementHandler,
} from '@aurora';
import { QueryStatement } from './sql-statement';

/**
 * Converts defaultSort parameter to GridSortState format.
 *
 * @param {any[]} defaultSort - The default sort in array format.
 * @returns {GridSortState | undefined} The converted GridSortState or undefined if invalid.
 */
const convertSequelizeSortToGridSortState = (
    defaultSort: Array<string | [string, string] | [string, string, string]>
): GridSortState | undefined => {
    if (!Array.isArray(defaultSort) || defaultSort.length < 2) {
        return undefined;
    }

    return {
        active: defaultSort[0] as string,
        direction: defaultSort[1] as SortDirection,
    };
};

/**
 * Handles the configuration of the query statement for the grid.
 *
 * This function manages column filters, sorting, pagination, and search state
 * from the grid services. It also ensures a deterministic sort order by
 * appending a default sort column if it's not present.
 *
 * @param {object} params - The configuration parameters.
 * @param {GridFiltersStorageService} [params.gridFiltersStorageService] - Service to manage grid filters.
 * @param {GridStateService} [params.gridStateService] - Service to manage grid state (sort, page, search).
 * @param {string} [params.gridId] - The ID of the grid.
 * @param {ColumnConfig[]} [params.columnsConfig] - Configuration for the grid columns.
 * @param {QueryStatement} [params.query] - The base query statement.
 * @param {any[]} [params.defaultSort] - The default sort order to apply (default: ['rowId', 'desc']).
 * @returns {QueryStatement} The configured query statement.
 */
export const gridQueryHandler = ({
    gridFiltersStorageService,
    gridStateService,
    gridId = null,
    columnsConfig = [],
    query = null,
    defaultSort = ['rowId', 'desc'],
}: {
    gridFiltersStorageService?: GridFiltersStorageService;
    gridStateService?: GridStateService;
    gridId?: string;
    columnsConfig?: ColumnConfig[];
    query?: QueryStatement;
    defaultSort?: Array<string | [string, string] | [string, string, string]>;
} = {}): QueryStatement => {
    const queryStatement = query
        ? query
        : queryStatementHandler({
              columnsConfig: columnsConfig,
          })
              .setColumFilters(
                  gridFiltersStorageService.getColumnFilterState(gridId)
              )
              .setSort(
                  gridStateService.getSort(
                      gridId,
                      convertSequelizeSortToGridSortState(defaultSort)
                  )
              )
              .setPage(gridStateService.getPage(gridId))
              .setSearch(gridStateService.getSearchState(gridId))
              .getQueryStatement();

    // Validate and filter sort entries
    const isValidDirection = (value: any): boolean =>
        value === 'asc' ||
        value === 'desc' ||
        value === 'ASC' ||
        value === 'DESC';

    if (queryStatement.order?.length > 0) {
        // Filter out invalid sort entries (those without a valid direction)
        queryStatement.order = queryStatement.order.filter(
            (sort) =>
                Array.isArray(sort) &&
                sort.length > 0 &&
                isValidDirection(sort[sort.length - 1])
        );

        // If we have valid sorts, add the default sort for determinism
        if (queryStatement.order.length > 0) {
            const primarySortDirection =
                queryStatement.order[0][queryStatement.order[0].length - 1] ||
                defaultSort[1] ||
                'desc';

            if (
                !queryStatement.order.some((sort) => sort[0] === defaultSort[0])
            ) {
                queryStatement.order.push([
                    defaultSort[0],
                    primarySortDirection,
                ]);
            }
            return queryStatement;
        }
    }

    // No valid sorts found, use default
    queryStatement.order = [defaultSort];

    return queryStatement;
};
